import {
  BarSeries,
  Category,
  Chart,
  ColumnSeries,
  DataLabel,
  FontModel,
  IAxisLabelClickEventArgs,
  IAxisLabelRenderEventArgs,
  ILegendClickEventArgs,
  ILegendRenderEventArgs,
  IPointEventArgs,
  ITextRenderEventArgs,
  Legend,
  SeriesModel,
  StackingBarSeries,
  StackingColumnSeries,
  Tooltip,
} from '@syncfusion/ej2-charts';
import { DataManager, UrlAdaptor } from '@syncfusion/ej2-data';
import { DropDownList } from '@syncfusion/ej2-dropdowns';

import {
  OrganisationDeadlineMonthlyView,
  OrganisationDeadlineView,
  ServiceDeadlineMonthlyView,
  ServiceStatsView,
} from '@prisma/client';
import Cookies from 'js-cookie';

import { FONT_FAMILY } from '@/shared/constants';
import { COOKIES } from '@/shared/cookies';
import { routes } from '@/shared/routes';
import { DashboardView, DueTimeFrame, DueTimeFrameType, TaskStatus } from '@/shared/types/enum';
import { enumOptions, omitEnumValues } from '@/shared/utils/enum';
import { formatEnumKey, formatLongMonth } from '@/shared/utils/format';
import { appendQuery, organisationsFilterUrl, tasksFilterUrl } from '@/shared/utils/url';
import { utcAddMonths, utcToday } from '@/shared/utils/utc';

const NEW_DEADLINE_PALETTES = ['#00B5E0', '#003643', '#7C4FFF', '#D93166', '#38CC9A'];
const NEW_MONTHLY_DEADLINE_PALETTES = ['#00B5E0', '#003643', '#38CC9A'];

const OLD_DEADLINE_PALETTES = ['#01CBCD', '#FFC107', '#FF8C00', '#DC3545', '#198754'];
const OLD_MONTHLY_DEADLINE_PALETTES = ['#01CBCD', '#0A415C', '#5C636A'];

const isTaskDeadlinesView = (view: string | undefined) =>
  view === DashboardView.TaskDeadlines || view === DashboardView.PartnerTaskDeadlines;

const partnerFilterParams = () => {
  const view = Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name);
  const partnerId = Cookies.get(COOKIES.PARTNER_ID_FILTER.name);

  if (view === DashboardView.PartnerTaskDeadlines && partnerId) {
    return { partnerId };
  }

  return {};
};

const clearConflictingDashboardFilters = (nextView: string, previousView: string | undefined) => {
  if (nextView === DashboardView.PartnerTaskDeadlines) {
    Cookies.remove(COOKIES.USER_ID_FILTER.name);
  }

  if (previousView === DashboardView.PartnerTaskDeadlines && nextView !== DashboardView.PartnerTaskDeadlines) {
    Cookies.remove(COOKIES.PARTNER_ID_FILTER.name);
  }
};

enum DeadlineType {
  PlannedStart = 'PlannedStart',
  PlannedCompletion = 'PlannedCompletion',
  Deadline = 'Deadline',
}

const roundRender = (args: any, series: { xName: string; yName: string }[], view: Record<string, unknown>[]) => {
  const category = args.point.x as string;
  const currentSeries = args.series as { xName: string; yName: string };
  const currentYName = currentSeries.yName;
  const point = args.point as any;

  // Skip if point value is zero
  if (!point.y || point.y === 0) {
    return;
  }

  // Find the data item for this category
  const dataItem = view.find((item) => item[currentSeries.xName] === category);
  if (!dataItem) {
    return;
  }

  // Collect all non-zero yNames for this category
  const nonZeroYNames: string[] = [];
  series.forEach((s) => {
    const value = dataItem[s.yName] as number;
    if (value && value > 0) {
      nonZeroYNames.push(s.yName as string);
    }
  });

  if (nonZeroYNames.length === 0) {
    return;
  }

  const firstVisibleYName = nonZeroYNames[0];
  const lastVisibleYName = nonZeroYNames[nonZeroYNames.length - 1];
  const R = 4;

  if (args.series) {
    if (nonZeroYNames.length === 1 && currentYName === firstVisibleYName) {
      // Single segment - round right corners only
      (args.series as any).cornerRadius = {
        topLeft: 0,
        topRight: R,
        bottomLeft: 0,
        bottomRight: R,
      };
    } else if (currentYName === lastVisibleYName) {
      // Last segment - round right corners only
      (args.series as any).cornerRadius = {
        topLeft: 0,
        bottomLeft: 0,
        topRight: R,
        bottomRight: R,
      };
    } else {
      // Middle segment - no rounding
      (args.series as any).cornerRadius = {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0,
      };
    }
  }
};

Chart.Inject(ColumnSeries, StackingColumnSeries, BarSeries, StackingBarSeries, Category, DataLabel, Legend, Tooltip);

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('dashboard')) {
    initViewPicker();
    initUserPicker();

    const view = Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name);

    if (view === DashboardView.OrganisationDeadlines) {
      initOrganisationDeadlineChart();
      initOrganisationDeadlineMonthlyChart();
    } else if (isTaskDeadlinesView(view)) {
      initDeadlineTypePicker();
      initTaskDeadlineChart();
      initTaskDeadlineMonthlyChart();
    }
  }
});

const initViewPicker = () => {
  const input = document.getElementById('view-picker') as HTMLInputElement;
  const hasPartners = input.dataset.hasPartners === 'true';

  const dataSource = enumOptions(
    hasPartners ? DashboardView : omitEnumValues(DashboardView, [DashboardView.PartnerTaskDeadlines]),
  );

  const viewPicker = new DropDownList({
    dataSource,
    fields: {
      value: 'value',
      text: 'text',
    },
    value: Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name),
    showClearButton: false,
    change: () => {
      const view = viewPicker.value as string;
      const previousView = Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name);

      clearConflictingDashboardFilters(view, previousView);
      Cookies.set(COOKIES.DASHBOARD_VIEW.name, view);

      location.reload();
    },
  });

  viewPicker.appendTo(input);
};

const initUserPicker = () => {
  const input = document.getElementById('user-picker') as HTMLInputElement;
  const partnerMode = input.dataset.mode === 'partner';

  const cookie = partnerMode ? COOKIES.PARTNER_ID_FILTER : COOKIES.USER_ID_FILTER;
  const emptyText = partnerMode ? 'All Partners' : 'All Users';

  const userPicker = new DropDownList({
    dataSource: new DataManager({
      url: appendQuery(routes.data.lookup.list({ model: 'MemberView' }), {
        includeEmpty: 'true',
        emptyText,
        ...(partnerMode ? { filter: JSON.stringify({ isPartner: true }) } : {}),
      }),
      adaptor: new UrlAdaptor(),
    }),
    fields: {
      value: 'value',
      text: 'text',
    },
    placeholder: emptyText,
    sortOrder: 'Ascending',
    value: Cookies.get(cookie.name),
    change: () => {
      const selectedId = userPicker.value as string;

      if (selectedId) {
        Cookies.set(cookie.name, selectedId);
      } else {
        Cookies.remove(cookie.name);
      }

      location.reload();
    },
  });

  userPicker.appendTo(input);
};

let taskDeadlineMonthlyChartInstance: Chart | null = null;

const initDeadlineTypePicker = () => {
  const input = document.getElementById('deadline-type-picker') as HTMLInputElement;

  if (!input) {
    return;
  }

  const deadlineTypePicker = new DropDownList({
    dataSource: [
      { value: DeadlineType.PlannedStart, text: 'Planned start date' },
      { value: DeadlineType.PlannedCompletion, text: 'Planned completion date' },
      { value: DeadlineType.Deadline, text: 'Deadline date' },
    ],
    fields: {
      value: 'value',
      text: 'text',
    },
    value: Cookies.get(COOKIES.DASHBOARD_DEADLINE_TYPE.name) ?? DeadlineType.Deadline,
    showClearButton: false,
    width: '220px',
    change: () => {
      const deadlineType = deadlineTypePicker.value as string;

      // Save scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      Cookies.set(COOKIES.DASHBOARD_DEADLINE_TYPE.name, deadlineType);

      // Reinitialise chart with new deadline type
      if (taskDeadlineMonthlyChartInstance) {
        taskDeadlineMonthlyChartInstance.destroy();
        taskDeadlineMonthlyChartInstance = null;
      }

      const element = document.getElementById('monthly-deadline-chart') as HTMLDivElement;
      if (element) {
        // Clear the element but preserve data
        const data = element.dataset.data;
        element.innerHTML = '';

        if (data) {
          element.innerHTML = `<div class="chart-placeholder d-none d-flex justify-content-center align-items-center" style="height: 500px">
            <div class="text-more-muted text-center">
              <p><i class="fa-duotone fa-chart-simple-horizontal" style="font-size: 5rem; opacity: 0.5"></i></p>
              <p>No deadlines to display</p>
            </div>
          </div>`;
          element.dataset.data = data;
          initTaskDeadlineMonthlyChart();

          // Restore scroll position after chart is rendered
          // Use setTimeout to ensure chart is fully rendered
          // It is not smart but it works
          setTimeout(() => {
            window.scrollTo({
              top: scrollPosition,
              behavior: 'instant' as ScrollBehavior,
            });
          }, 100);
        }
      }
    },
  });

  deadlineTypePicker.appendTo(input);
};

const initOrganisationDeadlineChart = () => {
  const element = document.getElementById('deadline-chart') as HTMLDivElement;

  const organisationDeadlineViews: OrganisationDeadlineView[] = JSON.parse(element.dataset.data!);

  if (organisationDeadlineViews.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type OrganisationDeadlineViewSeries = SeriesModel & {
      xName: keyof OrganisationDeadlineView;
      yName: keyof OrganisationDeadlineView;
    };

    const organisationDeadlineViewSeries = (
      props: Pick<OrganisationDeadlineViewSeries, 'name' | 'xName' | 'yName'>,
    ): OrganisationDeadlineViewSeries => {
      return {
        ...props,
        type: 'StackingBar100',
        dataSource: organisationDeadlineViews,
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
      };
    };

    const series: OrganisationDeadlineViewSeries[] = [
      organisationDeadlineViewSeries({
        name: 'Start overdue',
        xName: 'type',
        yName: 'due30To60Days',
      }),
      organisationDeadlineViewSeries({
        name: 'Due to complete',
        xName: 'type',
        yName: 'due30Days',
      }),
      organisationDeadlineViewSeries({
        name: 'Completed (30 days)',
        xName: 'type',
        yName: 'due',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        edgeLabelPlacement: 'Shift',
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: [deadlinePalettes()[0], deadlinePalettes()[2], deadlinePalettes()[3]],
      legendSettings: {
        visible: false,
      },
      height: `${organisationDeadlineViews.length * 50}px`,
      pointRender: (args: any) => {
        roundRender(args, series, organisationDeadlineViews);
      },
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        args.text = formatEnumKey(args.text);
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const dueTimeFrameType = args.point.x as DueTimeFrameType;

        const { yName: dueTimeFrame } = args.series;

        if (dueTimeFrame === 'due30To60Days') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueWithin30To60Days,
            dueTimeFrameType,
          });
        } else if (dueTimeFrame === 'due30Days') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueWithin30Days,
            dueTimeFrameType,
          });
        } else if (dueTimeFrame === 'due') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.Due,
            dueTimeFrameType,
          });
        }
      },
    });

    chart.appendTo(element);
    taskDeadlineMonthlyChartInstance = chart;
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initOrganisationDeadlineMonthlyChart = () => {
  const element = document.getElementById('monthly-deadline-chart') as HTMLDivElement;

  const items: OrganisationDeadlineMonthlyView[] = JSON.parse(element.dataset.data!);

  if (items.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type Series = SeriesModel & {
      xName: keyof OrganisationDeadlineMonthlyView;
      yName: keyof OrganisationDeadlineMonthlyView;
    };

    const serviceViewSeries = (props: Pick<Series, 'name' | 'xName' | 'yName' | 'groupName'>): Series => {
      return {
        ...props,
        type: 'Column',
        dataSource: items,
        cornerRadius: {
          topLeft: 4,
          topRight: 4,
        },
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
        enableTooltip: true,
      };
    };

    const series: Series[] = [
      serviceViewSeries({
        name: formatLongMonth(utcToday()),
        xName: 'type',
        yName: 'month1DueCount',
        groupName: 'month1',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 1)),
        xName: 'type',
        yName: 'month2DueCount',
        groupName: 'month2',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 2)),
        xName: 'type',
        yName: 'month3DueCount',
        groupName: 'month3',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 1 },
        majorTickLines: { width: 1 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: monthlyDeadlinePalettes(),
      legendSettings: {
        textStyle: font,
        padding: 20,
        toggleVisibility: false,
      },
      legendClick: (args: ILegendClickEventArgs) => {
        const { yName: countName } = args.series as Series;

        if (countName === 'month1DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
          });
        } else if (countName === 'month2DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
          });
        } else if (countName === 'month3DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
          });
        }
      },
      height: '500px',
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        args.text = formatEnumKey(args.text);
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const dueTimeFrameType = args.point.x as DueTimeFrameType;

        const { yName: countName } = args.series as Series;

        if (countName === 'month1DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            dueTimeFrameType,
          });
        } else if (countName === 'month2DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            dueTimeFrameType,
          });
        } else if (countName === 'month3DueCount') {
          location.href = organisationsFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            dueTimeFrameType,
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initTaskDeadlineChart = () => {
  const element = document.getElementById('deadline-chart') as HTMLDivElement;

  const serviceViews: ServiceStatsView[] = JSON.parse(element.dataset.data!);

  if (serviceViews.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type ServiceStatsViewSeries = SeriesModel & { xName: keyof ServiceStatsView; yName: keyof ServiceStatsView };

    const serviceViewSeries = (
      props: Pick<ServiceStatsViewSeries, 'name' | 'xName' | 'yName'>,
    ): ServiceStatsViewSeries => {
      return {
        ...props,
        type: 'StackingBar100',
        dataSource: serviceViews,
        // cornerRadius will be set dynamically in pointRender
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
      };
    };

    const series: ServiceStatsViewSeries[] = [
      serviceViewSeries({
        name: 'Start overdue',
        xName: 'name',
        yName: 'startDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Started',
        xName: 'name',
        yName: 'startedTaskCount',
      }),
      serviceViewSeries({
        name: 'Due to complete',
        xName: 'name',
        yName: 'completionDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Overdue',
        xName: 'name',
        yName: 'deadlineDateDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Completed (30 days)',
        xName: 'name',
        yName: 'completed30DaysTaskCount',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        edgeLabelPlacement: 'Shift',
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: deadlinePalettes(),
      legendSettings: {
        visible: false,
      },
      height: `${serviceViews.length * 50}px`,
      pointRender: (args: any) => {
        roundRender(args, series, serviceViews);
      },
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        if (args.text === '(No Service)') {
          args.labelStyle.color = '#adb5bd';
        }
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      axisLabelClick: (args: IAxisLabelClickEventArgs) => {
        const { text: serviceName } = args;
        const { id: serviceId } = serviceViews.filter((serviceView) => serviceView.name === serviceName)[0];

        location.href = tasksFilterUrl({ serviceId, preset: 'uncompleted', ...partnerFilterParams() });
      },
      pointClick: (args: IPointEventArgs) => {
        const { x: serviceName } = args.point;
        const { yName: countName } = args.series as ServiceStatsViewSeries;

        const { id: serviceId } = serviceViews.filter((serviceView) => serviceView.name === serviceName)[0];

        if (countName === 'startDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.StartDue,
            ...partnerFilterParams(),
          });
        } else if (countName === 'startedTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.Started,
            ...partnerFilterParams(),
          });
        } else if (countName === 'completionDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.CompletionDue,
            ...partnerFilterParams(),
          });
        } else if (countName === 'deadlineDateDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.DeadlineDue,
            ...partnerFilterParams(),
          });
        } else if (countName === 'completed30DaysTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            preset: 'completed30Days',
            ...partnerFilterParams(),
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initTaskDeadlineMonthlyChart = () => {
  const element = document.getElementById('monthly-deadline-chart') as HTMLDivElement;

  const items: ServiceDeadlineMonthlyView[] = JSON.parse(element.dataset.data!);

  const deadlineType = (Cookies.get(COOKIES.DASHBOARD_DEADLINE_TYPE.name) ?? DeadlineType.Deadline) as DeadlineType;

  if (items.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type Series = SeriesModel & { xName: keyof ServiceDeadlineMonthlyView; yName: keyof ServiceDeadlineMonthlyView };

    const getFieldName = (month: 1 | 2 | 3, suffix: 'TaskCount' | 'CompletedTaskCount') => {
      const monthPrefix = `month${month}`;
      if (deadlineType === DeadlineType.PlannedStart) {
        return `${monthPrefix}Planned${suffix}` as keyof ServiceDeadlineMonthlyView;
      } else if (deadlineType === DeadlineType.PlannedCompletion) {
        return suffix === 'CompletedTaskCount'
          ? (`${monthPrefix}PlannedCompletionCompletedTaskCount` as keyof ServiceDeadlineMonthlyView)
          : (`${monthPrefix}PlannedCompletionTaskCount` as keyof ServiceDeadlineMonthlyView);
      } else {
        return suffix === 'CompletedTaskCount'
          ? (`${monthPrefix}CompletedTaskCount` as keyof ServiceDeadlineMonthlyView)
          : (`${monthPrefix}Due${suffix}` as keyof ServiceDeadlineMonthlyView);
      }
    };

    const serviceViewSeries = (
      props: Pick<Series, 'name' | 'xName' | 'yName' | 'groupName' | 'stackingGroup' | 'opacity'>,
    ): Series => {
      return {
        ...props,
        type: 'StackingColumn',
        dataSource: items,
        cornerRadius: {
          topLeft: 4,
          topRight: 4,
        },
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
        enableTooltip: true,
      };
    };

    const series: Series[] = [
      serviceViewSeries({
        name: formatLongMonth(utcToday()),
        xName: 'name',
        yName: getFieldName(1, 'TaskCount'),
        groupName: 'month1',
        stackingGroup: 'month1',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 1)),
        xName: 'name',
        yName: getFieldName(2, 'TaskCount'),
        groupName: 'month2',
        stackingGroup: 'month2',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 2)),
        xName: 'name',
        yName: getFieldName(3, 'TaskCount'),
        groupName: 'month3',
        stackingGroup: 'month3',
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcToday())} (completed)`,
        xName: 'name',
        yName: getFieldName(1, 'CompletedTaskCount'),
        groupName: 'month1',
        stackingGroup: 'month1',
        opacity: 0.2,
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcAddMonths(utcToday(), 1))} (completed)`,
        xName: 'name',
        yName: getFieldName(2, 'CompletedTaskCount'),
        groupName: 'month2',
        stackingGroup: 'month2',
        opacity: 0.2,
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcAddMonths(utcToday(), 2))} (completed)`,
        xName: 'name',
        yName: getFieldName(3, 'CompletedTaskCount'),
        groupName: 'month3',
        stackingGroup: 'month3',
        opacity: 0.2,
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 1 },
        majorTickLines: { width: 1 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: monthlyDeadlinePalettes(),
      legendSettings: {
        textStyle: font,
        padding: 20,
        toggleVisibility: false,
      },
      height: '500px',
      legendRender: (args: ILegendRenderEventArgs) => {
        if (args.text.endsWith('(completed)')) {
          args.cancel = true;
        }
      },
      legendClick: (args: ILegendClickEventArgs) => {
        const { yName: countName } = args.series as Series;

        const month1Field = getFieldName(1, 'TaskCount');
        const month2Field = getFieldName(2, 'TaskCount');
        const month3Field = getFieldName(3, 'TaskCount');

        const dueTimeFrameField =
          deadlineType === DeadlineType.PlannedStart
            ? 'plannedStartDate'
            : deadlineType === DeadlineType.PlannedCompletion
              ? 'plannedCompletionDate'
              : 'deadlineDate';

        if (countName === month1Field) {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            dueTimeFrameField: dueTimeFrameField,
            preset: 'uncompleted',
            ...partnerFilterParams(),
          });
        } else if (countName === month2Field) {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            dueTimeFrameField: dueTimeFrameField,
            preset: 'uncompleted',
            ...partnerFilterParams(),
          });
        } else if (countName === month3Field) {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            dueTimeFrameField: dueTimeFrameField,
            preset: 'uncompleted',
            ...partnerFilterParams(),
          });
        }
      },
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        if (args.text === '(No Service)') {
          args.labelStyle.color = '#adb5bd';
        }
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const { x: serviceName } = args.point;
        const { yName: countName } = args.series as Series;
        const { id: serviceId } = items.filter((item) => item.name === serviceName)[0];

        const month1Field = getFieldName(1, 'TaskCount');
        const month2Field = getFieldName(2, 'TaskCount');
        const month3Field = getFieldName(3, 'TaskCount');
        const month1CompletedField = getFieldName(1, 'CompletedTaskCount');
        const month2CompletedField = getFieldName(2, 'CompletedTaskCount');
        const month3CompletedField = getFieldName(3, 'CompletedTaskCount');

        const dueTimeFrameField =
          deadlineType === DeadlineType.PlannedStart
            ? 'plannedStartDate'
            : deadlineType === DeadlineType.PlannedCompletion
              ? 'plannedCompletionDate'
              : 'deadlineDate';

        const darkBarPreset = 'uncompleted';

        if (countName === month1Field) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            dueTimeFrameField: dueTimeFrameField,
            preset: darkBarPreset,
            ...partnerFilterParams(),
          });
        } else if (countName === month2Field) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            dueTimeFrameField: dueTimeFrameField,
            preset: darkBarPreset,
            ...partnerFilterParams(),
          });
        } else if (countName === month3Field) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            dueTimeFrameField: dueTimeFrameField,
            preset: darkBarPreset,
            ...partnerFilterParams(),
          });
        } else if (countName === month1CompletedField) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            dueTimeFrameField: dueTimeFrameField,
            status: TaskStatus.Completed,
            ...partnerFilterParams(),
          });
        } else if (countName === month2CompletedField) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            dueTimeFrameField: dueTimeFrameField,
            status: TaskStatus.Completed,
            ...partnerFilterParams(),
          });
        } else if (countName === month3CompletedField) {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            dueTimeFrameField: dueTimeFrameField,
            status: TaskStatus.Completed,
            ...partnerFilterParams(),
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const isNewTheme = () => {
  return document.body.classList.contains('new-theme');
};

const deadlinePalettes = () => {
  return isNewTheme() ? NEW_DEADLINE_PALETTES : OLD_DEADLINE_PALETTES;
};

const monthlyDeadlinePalettes = () => {
  return isNewTheme() ? NEW_MONTHLY_DEADLINE_PALETTES : OLD_MONTHLY_DEADLINE_PALETTES;
};
